<template>
  <div class="searchable-select" :class="{ 'is-disabled': disabled }">
    <div class="position-relative">
      <input
        ref="input"
        type="text"
        class="form-control form-control-sm"
        :placeholder="!selectedLabel ? placeholder : ''"
        :value="inputDisplay"
        :disabled="disabled"
        @focus="openDropdown"
        @click="openDropdown"
        @input="handleInput"
        @keydown.down.prevent="highlightNext"
        @keydown.up.prevent="highlightPrevious"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc.prevent="closeDropdown"
      />
    </div>
    <div
      v-if="isOpen"
      ref="menu"
      class="dropdown-menu show w-100 shadow searchable-select__menu"
    >
      <div v-if="filteredOptions.length" class="list-group list-group-flush">
        <button
          v-for="(option, idx) in filteredOptions"
          :key="option[valueKey]"
          type="button"
          class="list-group-item list-group-item-action"
          :class="{ active: idx === highlightedIndex }"
          @mousedown.prevent="selectOption(option)"
        >
          <div class="font-weight-semibold">{{ option[labelKey] }}</div>
          <small v-if="subLabelKey && option[subLabelKey]" class="text-muted">
            {{ option[subLabelKey] }}
          </small>
        </button>
      </div>
      <p v-else class="text-muted small mb-0 px-3 py-2">{{ noResultsText }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    value: {
      type: [Number, String, null],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    valueKey: {
      type: String,
      default: 'id',
    },
    labelKey: {
      type: String,
      default: 'label',
    },
    subLabelKey: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Search...',
    },
    noResultsText: {
      type: String,
      default: 'No results found',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      searchTerm: '',
      highlightedIndex: 0,
    };
  },
  computed: {
    selectedOption() {
      return (this.options || []).find((option) => option[this.valueKey] === this.value) || null;
    },
    selectedLabel() {
      if (!this.selectedOption) return '';
      const main = this.selectedOption[this.labelKey] || '';
      const sub = this.subLabelKey ? (this.selectedOption[this.subLabelKey] || '') : '';
      return sub ? `${main} (${sub})` : main;
    },
    inputDisplay() {
      return this.isOpen ? this.searchTerm : this.selectedLabel;
    },
    filteredOptions() {
      const term = (this.searchTerm || '').toLowerCase();
      if (!term) return this.options;
      return (this.options || []).filter((option) => {
        const main = (option[this.labelKey] || '').toLowerCase();
        const sub = this.subLabelKey ? (option[this.subLabelKey] || '').toLowerCase() : '';
        return main.includes(term) || sub.includes(term);
      });
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.highlightedIndex = 0;
        document.addEventListener('click', this.handleClickOutside);
      } else {
        document.removeEventListener('click', this.handleClickOutside);
        this.resetSearch();
      }
    },
    filteredOptions(newOptions) {
      if (!newOptions.length) {
        this.highlightedIndex = 0;
      } else if (this.highlightedIndex >= newOptions.length) {
        this.highlightedIndex = 0;
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    openDropdown() {
      if (this.disabled) return;
      this.isOpen = true;
      this.searchTerm = '';
      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.select();
        }
      });
    },
    closeDropdown() {
      this.isOpen = false;
    },
    handleInput(event) {
      if (this.disabled) return;
      if (!this.isOpen) {
        this.openDropdown();
      }
      this.searchTerm = event.target.value;
    },
    resetSearch() {
      this.searchTerm = '';
      this.highlightedIndex = 0;
    },
    selectOption(option) {
      const value = option[this.valueKey];
      this.$emit('input', value);
      this.$emit('change', option);
      this.closeDropdown();
    },
    highlightNext() {
      if (!this.isOpen || !this.filteredOptions.length) return;
      this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredOptions.length;
    },
    highlightPrevious() {
      if (!this.isOpen || !this.filteredOptions.length) return;
      this.highlightedIndex =
        (this.highlightedIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
    },
    selectHighlighted() {
      if (!this.isOpen || !this.filteredOptions.length) return;
      const option = this.filteredOptions[this.highlightedIndex];
      if (option) {
        this.selectOption(option);
      }
    },
  },
};
</script>

<style scoped>
.searchable-select {
  position: relative;
}

.searchable-select__menu {
  max-height: 220px;
  overflow-y: auto;
  z-index: 1051;
}

.searchable-select.is-disabled {
  opacity: 0.65;
}

.font-weight-semibold {
  font-weight: 600;
}
</style>
